import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { LockKey, SignIn } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminLogin, getToken } from "@/lib/api";
import { BrandMark, COMPANY_NAME } from "@/components/Brand";

export default function AdminLogin() {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (getToken()) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password) {
      toast.error("Enter your admin password.");
      return;
    }
    setLoading(true);
    try {
      await adminLogin(password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Login failed";
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--epx-bg)" }}
      data-testid="admin-login-root"
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8 sm:p-10"
        style={{
          background: "var(--epx-surface)",
          borderColor: "var(--epx-border)",
        }}
      >
        <BrandMark size="lg" />
        <div className="mt-8">
          <p className="overline">Admin</p>
          <h2 className="font-heading text-3xl font-semibold mt-1">
            Sign in to your dashboard
          </h2>
          <p className="text-sm text-zinc-400 mt-2">
            {COMPANY_NAME} leads &amp; flake catalog.
          </p>
        </div>
        <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <div>
            <Label htmlFor="admin-pwd" className="text-zinc-300">
              Password
            </Label>
            <div className="relative mt-1.5">
              <LockKey
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <Input
                id="admin-pwd"
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-10 bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
                data-testid="admin-password-input"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 font-semibold text-white"
            style={{
              background: "var(--epx-primary)",
              boxShadow: "0 0 24px rgba(var(--epx-primary-rgb),0.30)",
            }}
            data-testid="admin-login-submit"
          >
            <SignIn size={16} weight="bold" className="mr-2" />
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-xs text-zinc-500 mt-6 text-center">
          Forgot it? Ask your developer to reset the{" "}
          <span className="font-mono-tech text-zinc-300">ADMIN_PASSWORD</span>{" "}
          env value.
        </p>
      </div>
    </div>
  );
}
